class ChatWidget extends HTMLElement {
  constructor() {
    super();

    this.endpoint = this.dataset.endpoint;
    this.leadEndpoint = this.dataset.leadEndpoint;
    this.leadCapturedKey = 'chat-widget-lead-captured';
    this.storageKey = 'chat-widget-history';
    this.autoOpenStorageKey = 'chat-widget-auto-opened';
    this.openCookieName = 'chat_widget_open';
    // On phones the open panel covers most of the screen, so it gets its own (longer) idle
    // delay. 740px matches the widget's mobile breakpoint in component-chat-widget.css.
    this.isSmallScreen = window.matchMedia('(max-width: 740px)').matches;
    this.idleOpenSeconds =
      Number(this.isSmallScreen ? this.dataset.idleOpenSecondsMobile : this.dataset.idleOpenSeconds) || 0;
    this.exitIntentMessage = this.dataset.exitIntentMessage || '';
    this.exitIntentShownKey = 'chat-widget-exit-intent-shown';
    this.historyExpiryMs = 30 * 60 * 1000; // reset the conversation after 30 minutes of inactivity

    this.toggleButton = this.querySelector('.chat-widget__toggle');
    this.panel = this.querySelector('.chat-widget__panel');
    this.closeButton = this.querySelector('.chat-widget__close');
    this.restartButton = this.querySelector('.chat-widget__restart');
    this.messagesEl = this.querySelector('.chat-widget__messages');
    this.form = this.querySelector('.chat-widget__form');
    this.input = this.querySelector('.chat-widget__input');

    this.initialMessagesHTML = this.messagesEl.innerHTML;

    this.history = this.loadHistory();
    this.conversationId = this.history.length > 0 ? this.readStoredConversationId() : null;
    if (!this.conversationId) this.conversationId = this.generateConversationId();
    this.renderHistory();

    this.toggleButton.addEventListener('click', () => this.toggle());
    this.closeButton.addEventListener('click', () => this.close());
    this.restartButton.addEventListener('click', () => this.restartConversation());
    this.addEventListener('keyup', (event) => event.code === 'Escape' && this.close());
    this.form.addEventListener('submit', this.onSubmit.bind(this));
    this.input.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        this.form.requestSubmit();
      }
    });
    this.input.addEventListener('input', () => this.autoGrowInput());

    // Delegated (not bound to the individual <a> elements) since restartConversation()
    // replaces messagesEl's innerHTML, which would otherwise detach direct listeners.
    this.messagesEl.addEventListener('click', (event) => {
      const quickAction = event.target.closest('.chat-widget__quick-action');
      if (!quickAction) return;

      const sent = quickAction.dataset.message
        ? this.sendMessage(quickAction.dataset.message)
        : Promise.resolve();

      // Let modified clicks (new-tab/new-window via keyboard or middle button) behave natively.
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const href = quickAction.href;
      if (!href || quickAction.getAttribute('href')?.charAt(0) === '#') return;

      let isExternal = false;
      try {
        isExternal = new URL(href, window.location.href).origin !== window.location.origin;
      } catch (_) {
        isExternal = false;
      }

      event.preventDefault();
      if (isExternal) {
        // Off-site link (e.g. Book a Demo on Calendly) - open a new tab so the chat stays put here.
        window.open(href, '_blank', 'noopener');
      } else {
        // Same-site link (e.g. Start Free Trial, Talk to Sales) - navigate in this tab, but
        // wait for the assistant reply to land and persist first (sendMessage saves each turn
        // to sessionStorage as it arrives). Otherwise the in-flight request is aborted by the
        // navigation and the widget reopens on the destination page showing only the user turn.
        // Cap the wait so a slow/hung backend can't block the click indefinitely.
        const navigate = () => window.location.assign(href);
        let navigated = false;
        const go = () => {
          if (navigated) return;
          navigated = true;
          navigate();
        };
        const safetyTimer = window.setTimeout(go, 6000);
        Promise.resolve(sent).finally(() => {
          window.clearTimeout(safetyTimer);
          go();
        });
      }
    });

    if (this.hasOpenCookie()) {
      this.open();
    } else if (this.idleOpenSeconds > 0 && !this.hasAutoOpened()) {
      this.setupIdleAutoOpen();
    }

    this.setupExitIntent();
  }

  getCookie(name) {
    return document.cookie.split('; ').find((entry) => entry.startsWith(`${name}=`))?.split('=')[1];
  }

  hasOpenCookie() {
    return this.getCookie(this.openCookieName) === '1';
  }

  setOpenCookie() {
    // No Max-Age/Expires - a session cookie that clears when the browser closes, same lifetime
    // as the sessionStorage-backed conversation history. Secure + SameSite=Lax since it's only
    // ever read/written by this same-site widget script, never sent to a server.
    document.cookie = `${this.openCookieName}=1; path=/; SameSite=Lax; Secure`;
  }

  clearOpenCookie() {
    document.cookie = `${this.openCookieName}=; path=/; SameSite=Lax; Secure; Max-Age=0`;
  }

  hasAutoOpened() {
    try {
      return window.sessionStorage.getItem(this.autoOpenStorageKey) === '1';
    } catch (error) {
      return false;
    }
  }

  markAutoOpened() {
    try {
      window.sessionStorage.setItem(this.autoOpenStorageKey, '1');
    } catch (error) {
      // sessionStorage unavailable - the idle popup may fire again on future pages this session.
    }
  }

  setupIdleAutoOpen() {
    const activityEvents = ['mousemove', 'keydown', 'scroll', 'touchstart'];

    const teardown = () => {
      clearTimeout(this.idleTimer);
      activityEvents.forEach((eventName) => document.removeEventListener(eventName, resetTimer));
    };

    const resetTimer = () => {
      clearTimeout(this.idleTimer);
      this.idleTimer = setTimeout(() => {
        if (!this.classList.contains('active')) {
          this.open({ auto: true });
          this.markAutoOpened();
        }
        teardown();
      }, this.idleOpenSeconds * 1000);
    };

    activityEvents.forEach((eventName) => document.addEventListener(eventName, resetTimer, { passive: true }));
    resetTimer();

    // Manually opening the widget means the visitor already found it - stop watching for idle.
    this.toggleButton.addEventListener(
      'click',
      () => {
        this.markAutoOpened();
        teardown();
      },
      { once: true },
    );
  }

  hasExitIntentShown() {
    try {
      return window.sessionStorage.getItem(this.exitIntentShownKey) === '1';
    } catch (error) {
      return false;
    }
  }

  markExitIntentShown() {
    try {
      window.sessionStorage.setItem(this.exitIntentShownKey, '1');
    } catch (error) {
      // sessionStorage unavailable - the exit-intent popup may fire again on future pages this session.
    }
  }

  setupExitIntent() {
    if (!this.exitIntentMessage || this.hasExitIntentShown() || this.hasOpenCookie()) return;

    if (window.matchMedia('(pointer: coarse)').matches) {
      this.setupMobileExitIntent();
    } else {
      this.setupDesktopExitIntent();
    }
  }

  triggerExitIntent() {
    const welcomeMessage = this.messagesEl.querySelector('.chat-widget__message--assistant');
    if (welcomeMessage) welcomeMessage.textContent = this.exitIntentMessage;

    this.open({ auto: true });
    this.markExitIntentShown();
  }

  setupDesktopExitIntent() {
    // mouseout + a null relatedTarget (nothing inside the page received the pointer) is the
    // standard cross-browser way to detect the cursor leaving the window - plain `mouseleave`
    // on document is inconsistent about firing when the cursor exits quickly toward the tab bar.
    // clientY <= 20 (not strictly <= 0) accounts for browsers reporting a small positive value
    // instead of an exact edge coordinate when the cursor exits near the top.
    const onMouseOut = (event) => {
      if (event.relatedTarget || event.toElement) return;
      if (event.clientY > 20 || this.classList.contains('active')) return;

      this.triggerExitIntent();
      document.removeEventListener('mouseout', onMouseOut);
    };

    document.addEventListener('mouseout', onMouseOut);
  }

  setupMobileExitIntent() {
    // No cursor to track on touch devices, so there's no direct equivalent of "heading toward
    // the tab bar". The closest proxy: a visitor scrolls down into the page (engaging with
    // content), then scrolls back up near the top again - often done right before switching
    // apps, hitting back, or reaching for the browser's address/tab bar.
    const ENGAGED_SCROLL_PX = 400;
    const RETURN_TO_TOP_PX = 80;
    let hasScrolledDeep = false;

    const onScroll = () => {
      const scrollY = window.scrollY;

      if (scrollY > ENGAGED_SCROLL_PX) {
        hasScrolledDeep = true;
        return;
      }

      if (!hasScrolledDeep || scrollY > RETURN_TO_TOP_PX || this.classList.contains('active')) return;

      this.triggerExitIntent();
      window.removeEventListener('scroll', onScroll);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
  }

  loadHistory() {
    try {
      const raw = window.sessionStorage.getItem(this.storageKey);
      if (!raw) return [];

      const parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.messages)) return [];
      if (Date.now() - parsed.updatedAt > this.historyExpiryMs) return [];

      return parsed.messages;
    } catch (error) {
      return [];
    }
  }

  readStoredConversationId() {
    try {
      const raw = window.sessionStorage.getItem(this.storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed?.conversationId || null;
    } catch (error) {
      return null;
    }
  }

  generateConversationId() {
    return window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  saveHistory() {
    try {
      window.sessionStorage.setItem(
        this.storageKey,
        JSON.stringify({ messages: this.history, updatedAt: Date.now(), conversationId: this.conversationId }),
      );
    } catch (error) {
      // sessionStorage unavailable (private browsing, quota, etc.) - conversation just won't persist across navigation.
    }
  }

  renderHistory() {
    this.history.forEach((message) => this.appendMessage(message.role, message.content));
  }

  restartConversation() {
    this.history = [];
    this.conversationId = this.generateConversationId();
    this.saveHistory();
    this.messagesEl.innerHTML = this.initialMessagesHTML;
    this.leadFormShown = false;
    this.input.value = '';
    this.autoGrowInput();
    this.input.focus();
  }

  hasLeadCaptured() {
    try {
      return window.sessionStorage.getItem(this.leadCapturedKey) === '1';
    } catch (error) {
      return false;
    }
  }

  markLeadCaptured() {
    try {
      window.sessionStorage.setItem(this.leadCapturedKey, '1');
    } catch (error) {
      // sessionStorage unavailable - the lead form may show again on future pages this session.
    }
  }

  showLeadCaptureForm() {
    this.leadFormShown = true;

    const strings = window.chatWidgetStrings || {};
    const form = document.createElement('form');
    form.className = 'chat-widget__lead-form';
    form.innerHTML = `
      <label class="visually-hidden" for="ChatWidget-LeadEmail">${strings.leadEmailPlaceholder || 'Your email'}</label>
      <input type="email" id="ChatWidget-LeadEmail" class="chat-widget__lead-input" placeholder="${
        strings.leadEmailPlaceholder || 'Your email'
      }" required />
      <button type="submit" class="chat-widget__lead-submit">${strings.leadSubmit || 'Send'}</button>
      <p class="chat-widget__lead-note">${strings.leadPrivacyNote || ''}</p>
    `;

    this.messagesEl.appendChild(form);
    this.scrollToLatest();

    form.addEventListener('submit', async (event) => {
      event.preventDefault();

      const emailInput = form.querySelector('.chat-widget__lead-input');
      const email = emailInput.value.trim();
      if (!email) return;

      const submitButton = form.querySelector('.chat-widget__lead-submit');
      submitButton.disabled = true;

      try {
        const response = await fetch(this.leadEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, messages: this.history }),
        });

        if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

        this.markLeadCaptured();
        form.remove();
        this.appendMessage('assistant', strings.leadSuccess || "Thanks! We'll follow up soon.");
      } catch (error) {
        submitButton.disabled = false;

        let errorEl = form.querySelector('.chat-widget__lead-error');
        if (!errorEl) {
          errorEl = document.createElement('p');
          errorEl.className = 'chat-widget__lead-error';
          form.appendChild(errorEl);
        }
        errorEl.textContent = strings.leadError || 'Something went wrong. Please try again.';
      }
    });
  }

  toggle() {
    if (this.classList.contains('active')) {
      this.close();
    } else {
      this.open();
    }
  }

  open({ auto = false } = {}) {
    this.classList.add('active');
    this.panel.hidden = false;
    this.toggleButton.setAttribute('aria-expanded', 'true');
    this.scrollToLatest();

    // An auto-open (idle / exit intent) is an offer, not a choice the visitor made: don't pull
    // focus into the input (which can raise the keyboard on phones) and don't set the cookie
    // that reopens the chat on every following page. Once they send a message it's theirs,
    // and sendMessage() sets the cookie then.
    if (auto) return;
    trapFocus(this.panel, this.input);
    this.setOpenCookie();
  }

  close() {
    if (!this.classList.contains('active')) return;
    this.classList.remove('active');
    this.panel.hidden = true;
    this.toggleButton.setAttribute('aria-expanded', 'false');
    removeTrapFocus();
    this.toggleButton.focus();
    this.clearOpenCookie();
  }

  autoGrowInput() {
    this.input.style.height = 'auto';
    this.input.style.height = `${Math.min(this.input.scrollHeight, 120)}px`;
  }

  appendMessage(role, content) {
    const message = document.createElement('div');
    message.className = `chat-widget__message chat-widget__message--${role}`;
    this.renderLinkedText(message, content);
    this.messagesEl.appendChild(message);
    this.scrollToLatest();
    return message;
  }

  // Turns markdown links [label](url) and bare URLs in the text into clickable
  // links, building the content from text nodes and anchor elements so the
  // message text is never treated as HTML.
  renderLinkedText(target, content) {
    const text = content == null ? '' : String(content);
    // Alternation: markdown link first, then bare http(s)/www URLs. Bare URLs
    // stop before trailing punctuation that is usually part of the sentence.
    const linkPattern =
      /\[([^\]]+)\]\(\s*((?:https?:\/\/|www\.)[^\s)]+)\s*\)|((?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)"'\]])/gi;
    let lastIndex = 0;
    let match;

    while ((match = linkPattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        target.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
      }

      if (match[2] !== undefined) {
        // Markdown link: match[1] is the label, match[2] is the URL.
        target.appendChild(this.createLink(match[2], match[1]));
      } else {
        // Bare URL, shown as its own text.
        target.appendChild(this.createLink(match[3], match[3]));
      }

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      target.appendChild(document.createTextNode(text.slice(lastIndex)));
    }
  }

  createLink(url, label) {
    const link = document.createElement('a');
    link.href = url.toLowerCase().startsWith('www.') ? `https://${url}` : url;
    link.textContent = label;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    return link;
  }

  scrollToLatest() {
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  showTypingIndicator() {
    const indicator = document.createElement('div');
    indicator.className = 'chat-widget__message chat-widget__message--assistant chat-widget__typing';
    indicator.setAttribute('aria-label', window.chatWidgetStrings?.thinking || 'Thinking...');
    indicator.innerHTML = `
      <span class="chat-widget__typing-dot" aria-hidden="true"></span>
      <span class="chat-widget__typing-dot" aria-hidden="true"></span>
      <span class="chat-widget__typing-dot" aria-hidden="true"></span>
    `;
    this.messagesEl.appendChild(indicator);
    this.scrollToLatest();
    return indicator;
  }

  onSubmit(event) {
    event.preventDefault();

    const content = this.input.value.trim();
    if (!content || this.isSending) return;

    this.input.value = '';
    this.autoGrowInput();
    this.sendMessage(content);
  }

  async sendMessage(content) {
    if (!content || this.isSending) return;

    // Engaging with an auto-opened chat makes it the visitor's own - keep it open across pages.
    if (this.classList.contains('active')) this.setOpenCookie();

    this.isSending = true;
    this.history.push({ role: 'user', content });
    this.appendMessage('user', content);
    this.saveHistory();
    const typingIndicator = this.showTypingIndicator();

    try {
      const response = await fetch(this.endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          messages: this.history,
          conversationId: this.conversationId,
          pageUrl: window.location.href,
        }),
      });

      if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

      const data = await response.json();
      if (!data.reply) throw new Error('Empty reply from chat service');

      typingIndicator.remove();
      this.history.push({ role: 'assistant', content: data.reply });
      this.appendMessage('assistant', data.reply);
      this.saveHistory();

      if (data.showLeadForm && this.leadEndpoint && !this.leadFormShown && !this.hasLeadCaptured()) {
        this.showLeadCaptureForm();
      }
    } catch (error) {
      typingIndicator.remove();
      this.appendMessage(
        'error',
        window.chatWidgetStrings?.error || 'Something went wrong. Please try again in a moment.',
      );
    } finally {
      this.isSending = false;
    }
  }
}

customElements.define('chat-widget', ChatWidget);
